import { useEffect, useRef, useState } from "react";
import { Users, X } from "lucide-react";
import {
  filterParticipantCandidates,
  type ParticipantCandidate,
} from "../utils/assessmentParticipants";

interface ParticipantMultiSelectProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  users: ParticipantCandidate[];
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /**
   * Open the dropdown upwards. Needed where the picker sits near the bottom of
   * a clipped container, in which case a downward list is cut off.
   */
  dropUp?: boolean;
}

/**
 * Multi-select user picker used to record who took part in an assessment.
 *
 * Deliberately plain: a text filter, a short dropdown and removable chips. It
 * holds user ids only; the backend snapshots the name and email at save time,
 * so nothing here needs to survive a rename.
 */
export default function ParticipantMultiSelect({
  selectedIds,
  onChange,
  users,
  loading = false,
  disabled = false,
  placeholder = "Search people by name or email",
  dropUp = false,
}: ParticipantMultiSelectProps) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const byId = new Map(users.map((u) => [u.id, u]));
  const candidates = filterParticipantCandidates(users, query, selectedIds);

  const add = (id: string) => {
    if (selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
    setQuery("");
  };

  const remove = (id: string) => {
    onChange(selectedIds.filter((existing) => existing !== id));
  };

  return (
    <div ref={containerRef} className="relative">
      {selectedIds.length > 0 && (
        <ul
          className="flex flex-wrap gap-2 mb-2"
          aria-label="Selected participants"
        >
          {selectedIds.map((id) => {
            const u = byId.get(id);
            // A selected id with no matching user means the directory list has
            // not loaded yet, or the account has gone. Show the id rather than
            // dropping the chip, so the selection is never silently lost.
            const label = u ? `${u.name} (${u.email})` : id;
            return (
              <li
                key={id}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200"
              >
                <span>{label}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(id)}
                    aria-label={`Remove ${label}`}
                    className="p-0.5 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!disabled && (
        <>
          <div className="relative">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={query}
              disabled={loading}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={loading ? "Loading people..." : placeholder}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {isOpen && (
            <div
              className={`absolute z-20 w-full max-h-60 overflow-auto bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg ${
                dropUp ? "bottom-full mb-1" : "mt-1"
              }`}
            >
              {candidates.length === 0 ? (
                <p className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {loading ? "Loading people..." : "No matching people"}
                </p>
              ) : (
                candidates.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => add(u.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100"
                  >
                    <span className="font-medium">{u.name}</span>{" "}
                    <span className="text-gray-500 dark:text-gray-400">
                      {u.email}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
